import Phaser from 'phaser';
import { NIGHT_ALLEY, SODIUM_UNDERPASS, COURTYARD_DUSK, type LightSkin, type LightDef } from '../light/LightSkin';
import type { ReflectiveZone } from '../light/reflective-ground';
import { groundBounds, screenPos } from '../game/ground';
import {
  parseColor,
  validateScene,
  type SceneDef,
  type SceneProp,
  type SceneWater,
} from './scene-format';

// The parts of a Scene the runtime resolves at load — the id→record lookups the
// JSON references by name, and the two pure translations from Scene data into the
// engine's own shapes (a prop's light, the water's reflective zones). Kept out of
// the Phaser Scene class so the class stays "run the location" and this stays
// "turn the record into runtime objects".

/** Where the parsed, validated active SceneDef is published for the runner. */
export const SCENE_REGISTRY_KEY = 'scene:def';
/** The default location, when no ?scene= is given. The Courtyard is the front
 *  door (Order 16.7, Matt's call) — the playable-cinema proof is the strongest
 *  work, so the bare URL opens it. The alley stays reachable via ?scene=alley
 *  and the courtyard's door exit. */
export const DEFAULT_SCENE_ID = 'courtyard';

/** The in-run override a taken exit installs — a transition is "set the next id,
 *  restart the load chain". Sticky until the next exit overwrites it, so a
 *  reloaded Preload keeps loading the location the player actually walked to. */
let sceneOverride: string | null = null;

/** A taken exit's handoff: the NEXT load chain will load this location. */
export function requestScene(id: string): void {
  sceneOverride = id;
}

/** An authoring-session source of truth for scene records. When installed (the
 *  Arcade Authoring shell, ?edit=1), the load chain asks it for a SceneDef
 *  BEFORE trusting the fetched JSON — so in-memory edits survive a restart or
 *  a location transition instead of being clobbered by the stale file on disk.
 *  Returns null for a scene it doesn't hold. NEVER installed by the runtime:
 *  without it the load chain is byte-identical to the pre-authoring engine. */
export type SceneSource = (id: string) => SceneDef | null;

let sceneSource: SceneSource | null = null;

export function installSceneSource(source: SceneSource | null): void {
  sceneSource = source;
}

/** The authoring session's record for `id`, if a source is installed and holds
 *  one. The PreloadScene consults this in create(). */
export function sourcedScene(id: string): SceneDef | null {
  return sceneSource?.(id) ?? null;
}

/** Which location to load. A taken exit's override wins; else `?scene=<id>`;
 *  absent → the alley. A second location is a JSON file plus this query param,
 *  no code. Guarded for non-browser contexts (tests, SSR) where location is
 *  absent. */
export function activeSceneId(): string {
  if (sceneOverride) return sceneOverride;
  const search = (globalThis as { location?: { search?: string } }).location?.search ?? '';
  const id = new URLSearchParams(search).get('scene');
  return id && /^[a-z0-9-]+$/.test(id) ? id : DEFAULT_SCENE_ID;
}

/** Phaser cache key / URL for a scene's JSON. */
export const sceneJsonKey = (id: string) => `scene:${id}`;
export const sceneUrl = (id: string) => `scenes/${id}.json`;

/** Validate a freshly-fetched Scene and hand back the typed record. A non-empty
 *  problem list is a HARD failure — a half-loaded location draws garbage, so it
 *  never gets that far. */
export function prepareScene(raw: unknown): SceneDef {
  const scene = raw as SceneDef;
  const problems = validateScene(scene);
  if (problems.length > 0) {
    throw new Error(`scene '${scene?.id ?? '?'}': invalid:\n  ${problems.join('\n  ')}`);
  }
  return scene;
}

/** Light skins referenced by id from a Scene's `lightSkin` field. Adding a mood
 *  is adding a const to LightSkin.ts and a line here — no Scene code changes. */
export const LIGHT_SKINS: Record<string, LightSkin> = {
  [NIGHT_ALLEY.id]: NIGHT_ALLEY,
  [SODIUM_UNDERPASS.id]: SODIUM_UNDERPASS,
  [COURTYARD_DUSK.id]: COURTYARD_DUSK,
};

export function resolveLightSkin(id: string): LightSkin {
  const skin = LIGHT_SKINS[id];
  if (!skin) throw new Error(`scene: unknown lightSkin '${id}' (have: ${Object.keys(LIGHT_SKINS).join(', ')})`);
  return skin;
}

/** Turn a prop's own light (offset from the prop) into a world-placed LightDef.
 *  This is the marquee coupling made concrete: the sign's (x, y) plus the light's
 *  (dx, dy) is the light's world position, so moving the sign moves its key. */
export function propLightDef(prop: SceneProp): LightDef | null {
  const l = prop.light;
  if (!l) return null;
  const def: LightDef = {
    role: l.role,
    x: prop.x + l.dx,
    y: prop.y + l.dy,
    radius: l.radius,
    color: parseColor(l.color),
    intensity: l.intensity,
  };
  if (l.flicker) def.flicker = l.flicker;
  return def;
}

/** Compose the effective lighting for a location: the referenced skin's placeless
 *  lights PLUS every prop's own light. Returns a fresh LightSkin value (the shared
 *  const is never mutated) that applyLightSkin consumes unchanged. */
export function effectiveSkin(scene: SceneDef): LightSkin {
  const base = resolveLightSkin(scene.lightSkin);
  const propLights = scene.props.map(propLightDef).filter((d): d is LightDef => d !== null);
  return { ...base, lights: [...base.lights, ...propLights] };
}

// The road zone deliberately OVERHANGS the walkable street and carries no feather
// (Order 16.4): bound exactly to the street, an actor at either kerb would sit on
// the zone's own soft edge and lose his reflection precisely where the road is
// most obviously wet. These overhangs are the engine's rule, not a Scene's
// business, so they live here rather than in the record.
const ROAD_X_OVERHANG = 40;
const ROAD_Z_OVERHANG = 8;

/** Build the reflective zones — rectangles of ground — from a Scene's water. The
 *  bare road spans the whole street (overhung, unfeathered); each puddle adds a
 *  stronger, feathered patch. Mirror plane is NOT here; it is the ground under the
 *  actor's own feet (reflective-ground.ts). */
export function reflectiveZones(water: SceneWater): ReflectiveZone[] {
  const b = groundBounds();
  const zones: ReflectiveZone[] = [
    {
      x0: b.xMin - ROAD_X_OVERHANG, x1: b.xMax + ROAD_X_OVERHANG,
      z0: b.zMin - ROAD_Z_OVERHANG, z1: b.zMax + ROAD_Z_OVERHANG,
      strength: water.road.strength, feather: 0, airGap: water.road.airGap,
    },
  ];
  for (const pd of water.puddles) {
    zones.push({
      x0: pd.x - pd.width / 2, x1: pd.x + pd.width / 2,
      z0: pd.z - pd.depth / 2, z1: pd.z + pd.depth / 2,
      strength: pd.strength, feather: pd.feather, airGap: pd.airGap,
    });
  }
  return zones;
}

/** Draw a prop and return its GameObject. The texture must already be baked/loaded
 *  (BootScene bakes the procedural set); a missing texture is a hard failure, not
 *  a silent invisible prop. */
export function placeProp(scene: Phaser.Scene, prop: SceneProp): Phaser.GameObjects.Image {
  if (!scene.textures.exists(prop.texture)) {
    throw new Error(`scene: prop '${prop.id}' references texture '${prop.texture}', which is not loaded`);
  }
  return scene.add.image(prop.x, prop.y, prop.texture).setDepth(prop.depth);
}

/** Screen centre of a puddle's visible ellipse, from its ground position. */
export function puddleScreenCentre(x: number, z: number): { x: number; y: number } {
  return screenPos(x, z, 0);
}
